import { useState } from "react"
import { Archive, Copy, Loader2 } from "lucide-react"
import { Link, Navigate } from "react-router"

import {
  type ArticleGenerateResponse,
  useGenerateArticle,
} from "@/api/hooks/articleGenerator"
import { useAuth } from "@/auth/AuthProvider"
import { ApiError } from "@/api/client"

import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS } from "./options"
import { SearchableMultiSelect, SearchableSelect } from "./SearchableSelect"

async function copyRichHtml(html: string, plain: string) {
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      })
      await navigator.clipboard.write([item])
      return
    }
  } catch {
    // fall through
  }
  // Fallback: copy HTML via a temporary contentEditable so many editors get rich paste.
  const holder = document.createElement("div")
  holder.contentEditable = "true"
  holder.innerHTML = html
  holder.style.position = "fixed"
  holder.style.left = "-9999px"
  document.body.appendChild(holder)
  const range = document.createRange()
  range.selectNodeContents(holder)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
  try {
    document.execCommand("copy")
  } finally {
    sel?.removeAllRanges()
    document.body.removeChild(holder)
  }
}

export default function ArticleGeneratorTab() {
  const { canUseArticleGenerator } = useAuth()
  const generate = useGenerateArticle()

  const [primaryTopic, setPrimaryTopic] = useState("")
  const [adText, setAdText] = useState("")
  const [countries, setCountries] = useState<string[]>([])
  const [language, setLanguage] = useState<string | null>(null)
  const [result, setResult] = useState<ArticleGenerateResponse | null>(null)
  const [copyState, setCopyState] = useState<"idle" | "body" | string>("idle")

  const TOPIC_MIN_CHARS = 100
  const topicLength = primaryTopic.trim().length
  const topicTooShort = topicLength < TOPIC_MIN_CHARS

  const canSubmit =
    !topicTooShort &&
    adText.trim().length >= 10 &&
    countries.length >= 1 &&
    !!language &&
    language.trim().length >= 2 &&
    !generate.isPending

  if (!canUseArticleGenerator) {
    return <Navigate to="/tools/url-encoder" replace />
  }

  async function handleGenerate() {
    if (!language) return
    // Drop any previous error so a stale 504 doesn’t flash on the next click.
    generate.reset()
    setResult(null)
    const data = await generate.mutateAsync({
      primary_topic: primaryTopic.trim(),
      ad_text: adText.trim(),
      countries,
      language: language.trim(),
    })
    const options =
      data.titles?.length > 0 ? data.titles : data.title ? [data.title] : []
    setResult({ ...data, titles: options })
  }

  async function handleCopyTitle(label: string) {
    await copyRichHtml(`<p>${escapeHtml(label)}</p>`, label)
    setCopyState(`title:${label}`)
    setTimeout(() => setCopyState("idle"), 2000)
  }

  async function handleCopyBody() {
    if (!result) return
    await copyRichHtml(result.body_html, stripHtml(result.body_html))
    setCopyState("body")
    setTimeout(() => setCopyState("idle"), 2000)
  }

  const errorMessage = (() => {
    if (!generate.error) return null
    if (generate.error instanceof ApiError) {
      if (typeof generate.error.detail === "string") {
        return generate.error.detail
      }
      const msg = generate.error.message
      if (
        generate.error.status === 504 ||
        /504|gateway timeout|<!DOCTYPE html>/i.test(msg)
      ) {
        return (
          "API gateway timeout (504). The app is likely restarting or unhealthy — " +
          "wait for deploy to finish, then retry."
        )
      }
      return msg
    }
    return generate.error instanceof Error
      ? generate.error.message
      : "Generation failed"
  })()

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="flex justify-end">
        <Link
          to="/tools/article-generator/archive"
          className="inline-flex items-center gap-1.5 rounded-md border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-sm text-white hover:bg-[#444]"
        >
          <Archive className="h-4 w-4" aria-hidden />
          Archive
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <fieldset className="rounded border border-[#555] px-4 pb-4 pt-1 md:col-span-2">
          <legend className="px-1 text-sm text-[#aaa]">
            Detailed Article Explanation
          </legend>
          <textarea
            value={primaryTopic}
            onChange={(e) => setPrimaryTopic(e.target.value)}
            rows={4}
            minLength={TOPIC_MIN_CHARS}
            className="w-full resize-y rounded border-0 bg-transparent text-sm text-white outline-none placeholder:text-[#666]"
            placeholder={
              "Explain what your topic should talk about. " +
              "e.g. Depression test — what it measures, who it helps, how results are explained (informational only); " +
              "AI writing tools — what they do, common use cases, how to compare options; " +
              "Fitness apps — how tracking works, what beginners should look for, free vs paid features"
            }
          />
          <p className="mt-2 text-xs text-[#888]">
            {topicTooShort
              ? `Tip: around ${TOPIC_MIN_CHARS}+ characters helps the model (${topicLength}/${TOPIC_MIN_CHARS}). A bit more detail goes a long way.`
              : `${topicLength} characters`}
          </p>
        </fieldset>

        <fieldset className="rounded border border-[#555] px-4 pb-4 pt-1 md:col-span-2">
          <legend className="px-1 text-sm text-[#aaa]">
            Ad Text (text from TikTok ad)
          </legend>
          <input
            value={adText}
            onChange={(e) => setAdText(e.target.value)}
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#666]"
            placeholder="e.g. Online mood check explained — what a short depression screening usually covers"
            minLength={10}
          />
        </fieldset>

        <SearchableMultiSelect
          label="Countries"
          options={COUNTRY_OPTIONS}
          values={countries}
          onChange={setCountries}
          placeholder="Search countries…"
        />

        <SearchableSelect
          label="Language"
          options={LANGUAGE_OPTIONS}
          value={language}
          onChange={setLanguage}
          placeholder="Search languages…"
        />
      </div>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => void handleGenerate()}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {generate.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Generating with Gemini… (can take 1–3 min)
          </>
        ) : (
          "Generate article"
        )}
      </button>

      {errorMessage ? (
        <p className="rounded border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {errorMessage}
        </p>
      ) : null}

      {result ? (
        <div className="space-y-4 border-t border-[#4a4a4a] pt-6">
          <p className="text-xs text-[#aaa]">
            {result.word_count} words · {result.h2_count} H2 · {result.provider} · readability{" "}
            {result.readability_band}
            {result.archive_id ? (
              <>
                {" · "}
                <Link
                  to={`/tools/article-generator/archive/${result.archive_id}`}
                  className="text-[#8b9aff] hover:underline"
                >
                  saved #{result.archive_id}
                </Link>
              </>
            ) : null}
          </p>

          <fieldset className="rounded border border-[#555] bg-[#252525] px-4 pb-3 pt-1">
            <legend className="px-1 text-sm text-[#aaa]">Article titles</legend>
            <div className="space-y-2">
              {(result.titles?.length ? result.titles : [result.title]).map(
                (option, index) => (
                  <div
                    key={`${index}-${option}`}
                    className="flex items-start gap-3 rounded-md border border-[#555] bg-[#2a2a2a] px-3 py-2.5"
                  >
                    <p className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-white">
                      {option}
                    </p>
                    <button
                      type="button"
                      aria-label={`Copy title ${index + 1}`}
                      onClick={() => void handleCopyTitle(option)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-sm text-white hover:bg-[#444]"
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      {copyState === `title:${option}` ? "Copied" : "Copy"}
                    </button>
                  </div>
                ),
              )}
            </div>
          </fieldset>

          <fieldset className="rounded border border-[#555] bg-[#252525] px-4 pb-4 pt-1">
            <legend className="px-1 text-sm text-[#aaa]">Article</legend>
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => void handleCopyBody()}
                className="inline-flex items-center gap-1.5 rounded border border-[#555] bg-[#3a3a3a] px-3 py-1.5 text-sm text-white hover:bg-[#444]"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
                {copyState === "body" ? "Copied" : "Copy"}
              </button>
            </div>
            <div
              className="space-y-3 text-[15px] leading-relaxed text-[#ddd] [&_a]:text-[#8b9aff] [&_a]:underline [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-white [&_li]:ml-4 [&_ul]:list-disc [&_ul]:space-y-1"
              dangerouslySetInnerHTML={{ __html: result.body_html }}
            />
          </fieldset>
        </div>
      ) : null}
    </div>
  )
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function stripHtml(html: string): string {
  const el = document.createElement("div")
  el.innerHTML = html
  return (el.textContent || el.innerText || "").replace(/\n{3,}/g, "\n\n").trim()
}
